import AppRouter from '@/app/AppRouter';
import { AppSentryArea } from '@/app/AppSentryArea';
import { AppRoutesPaths } from '@/common/constants';
import { REDIRECT_URL_KEY } from '@/common/constants/user';
import { AppContextComponent } from '@/common/context/AppContext';
import { LanguageContextComponent } from '@/common/context/LanguageContext';
import i18n from '@/i18n';
import { store } from '@/store';
import { parseEnviourmentUrl } from '@/utils/helpers';
import { getAppAbsoluteUrl } from '@/utils/url';
import { Toast } from 'primereact/toast';
import React, { createContext, useEffect, useRef } from 'react';
import { CookiesProvider } from 'react-cookie';
import { HelmetProvider } from 'react-helmet-async';
import { I18nextProvider } from 'react-i18next';
import {
  AuthProvider,
  AuthProviderNoUserManagerProps,
  useAuth,
} from 'react-oidc-context';
import { QueryClient, QueryClientProvider } from 'react-query';
import { Provider } from 'react-redux';
import { BrowserRouter } from 'react-router-dom';
import { AppHelmet } from './AppHelmet';
import { AppPrimeReactProvider } from './AppPrimeReactProvider';
import { AppSessionExpiryListener } from './AppSessionExpiryListener';
import { PostHogPageViewTracker, PosthogTrackingProvider } from './posthog';

const DEFAULT_KEYCLOAK_REALM = 'app';

// TODO: Simplify if it works correctly
const subdomain =
  window.location.host.replace('www.', '').split('.').length >= 2 &&
  process.env?.REACT_APP_FORCE_CONFIG_URLS !== 'true'
    ? window.location.host.split('.')[0]
    : process.env?.REACT_APP_KEYCLOAK_REALM &&
      process.env?.REACT_APP_KEYCLOAK_REALM !== ''
    ? process.env?.REACT_APP_KEYCLOAK_REALM
    : DEFAULT_KEYCLOAK_REALM;

const clientId =
  process.env?.REACT_APP_DEPLOYMENT_ENVIRONMENT === 'preview'
    ? `${subdomain}-preview`
    : subdomain;

const keycloakUrl = parseEnviourmentUrl().keycloakUrl;

const deployPath = process.env.REACT_APP_PREVIEW_DEPLOY_PATH;
const redirectPath =
  deployPath && deployPath !== ''
    ? `/${deployPath}/complete-sign-in`
    : '/complete-sign-in';
const STALE_STATE_AGE_IN_SECONDS = 900;

const oidcConfig: AuthProviderNoUserManagerProps = {
  authority: keycloakUrl ? `${keycloakUrl}realms/${subdomain}` : '',
  client_id: clientId,
  redirect_uri: window.location.origin + redirectPath,
  silent_redirect_uri: getAppAbsoluteUrl(
    AppRoutesPaths.SILENT_RENEW_CALLBACK_ROUTE,
  ),
  automaticSilentRenew: true,
  staleStateAgeInSeconds: STALE_STATE_AGE_IN_SECONDS,
};

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      refetchInterval: false,
      refetchIntervalInBackground: false,
    },
  },
});

export const GlobalToast = createContext({});

const App = () => {
  const toastRef = useRef(null);

  const helmetContext = {};

  const deployPath = process.env.REACT_APP_PREVIEW_DEPLOY_PATH;
  const basename = deployPath ? `/${deployPath}` : '/';

  return (
    <BrowserRouter
      basename={basename}
      future={{ v7_startTransition: true, v7_relativeSplatPath: true }}
    >
      <PostHogPageViewTracker />
      <AppSentryArea />
      <HelmetProvider context={helmetContext}>
        <GlobalToast.Provider value={toastRef}>
          <CookiesProvider defaultSetOptions={{ path: '/' }}>
            <I18nextProvider i18n={i18n}>
              <AppHelmet />
              <AppSessionExpiryListener />
              <Toast
                ref={toastRef}
                baseZIndex={9999}
                appendTo={document.body}
              />
              <AppRouter />
            </I18nextProvider>
          </CookiesProvider>
        </GlobalToast.Provider>
      </HelmetProvider>
    </BrowserRouter>
  );
};

const RedirectUrlTracker = () => {
  const { isAuthenticated, activeNavigator } = useAuth();

  useEffect(() => {
    if (
      !isAuthenticated &&
      activeNavigator !== 'signinRedirect' &&
      !window.location.pathname.match(/complete-sign-in|silent-renew-callback/g)
    ) {
      sessionStorage.setItem(
        REDIRECT_URL_KEY,
        `${window.location.pathname}${window.location.search}`,
      );
    }
  }, [isAuthenticated, activeNavigator, window.location.href]);

  return null;
};

const AppWithStore = () => (
  <AppPrimeReactProvider>
    <Provider store={store}>
      <QueryClientProvider client={queryClient}>
        <AppContextComponent>
          <LanguageContextComponent>
            <AuthProvider {...oidcConfig}>
              <RedirectUrlTracker />
              <PosthogTrackingProvider>
                <App />
              </PosthogTrackingProvider>
            </AuthProvider>
          </LanguageContextComponent>
        </AppContextComponent>
      </QueryClientProvider>
    </Provider>
  </AppPrimeReactProvider>
);

export default AppWithStore;
